import React, { useRef, useState, useEffect, useCallback } from "react";
import { CheckCircle, Star } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getAboutUsData } from "../../../api/aboutUsApi";
import { useTheme } from "../../../contexts/ThemeContext";
import WhyChooseUsSimple from "./WhyChooseUsSimple";
import ShimmerStyles, { ShimmerBox } from "../../../components/ShimmerEffect";

gsap.registerPlugin(ScrollTrigger, useGSAP);

/* ─────────────────────────────────────────────────────────────
   Industry-grade animated counter hook
   • Uses rAF (requestAnimationFrame) — no GSAP dependency
   • IntersectionObserver fires it every time the element enters
     the viewport (scroll down AND scroll back up)
   • Works perfectly with API-loaded values (target can change)
   • easeOutExpo curve for the "real product" feel
───────────────────────────────────────────────────────────── */
function useAnimatedCounter(target, isDecimal = false, duration = 2200) {
  const elRef = useRef(null);
  const rafRef = useRef(null);
  const observerRef = useRef(null);

  const fmt = useCallback(
    (v) =>
      isDecimal
        ? Number(v).toFixed(1)
        : Math.round(v).toLocaleString("en-IN"),
    [isDecimal]
  );

  // easeOutExpo — fast ramp-up, slow finish → premium feel
  const ease = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

  const runCounter = useCallback(() => {
    if (!elRef.current) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const value = target * ease(progress);
      if (elRef.current) elRef.current.textContent = fmt(value);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        if (elRef.current) elRef.current.textContent = fmt(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [target, duration, fmt]);

  useEffect(() => {
    if (!elRef.current) return;

    // Paint final value immediately — no blank flash
    elRef.current.textContent = fmt(target);

    // Disconnect previous observer before creating a new one
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) runCounter();
      },
      { threshold: 0.15 }
    );
    observerRef.current.observe(elRef.current);

    // If already visible on mount (e.g. page refresh) → play immediately
    const rect = elRef.current.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) runCounter();

    return () => {
      observerRef.current?.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, runCounter, fmt]);

  return elRef;
}

/* ─────────────────────────────────────────────────────────────
   Default fallback data
───────────────────────────────────────────────────────────── */
const DEFAULT_DATA = {
  header: {
    tag: "WHY US",
    title: "Built on trust. Backed by proof.",
    description:
      "Every laptop goes through our 49-point inspection, comes with a 12-month warranty, and is priced to feel like a smart upgrade.",
  },
  card1: {
    tag: "OUR PROMISE",
    title: "Why Bright Laptop?",
    points: [
      "20,000+ satisfied customers",
      "Serving individuals, startups & institutions",
      "75% less carbon footprint per device",
    ],
  },
  statsCards: [
    { tag: "TRUSTED BY",       target: 20000, suffix: "+",      isDecimal: false, title: "Happy customers",           description: "Thousands of students, teams, and creators buy refurbished through Bright every month." },
    { tag: "QUALITY CERTIFIED",target: 49,    suffix: "-Point", isDecimal: false, title: "Inspection on every device", description: "Battery, keyboard, ports, display, thermals, RAM, SSD, and performance are rigorously checked before any device ships." },
    { tag: "POCKET FRIENDLY",  target: 60,    suffix: "%",      isDecimal: false, title: "Savings on retail",          description: "Every refurbished laptop extends product life and keeps e-waste out of landfill." },
    { tag: "RISK FREE",        target: 14,    suffix: "-Day",   isDecimal: false, title: "No-questions returns",       description: "Free pickup support makes post-purchase risk feel much lower." },
    { tag: "FULL COVERAGE",    target: 12,    suffix: "-Month", isDecimal: false, title: "Warranty Included",          description: "Every laptop includes hassle-free warranty with free repair, service centre access, or replacement support built right in for a full year." },
    { tag: "RATED ON GOOGLE",  target: 4.8,   suffix: "",       isDecimal: true,  title: "",                           description: "From thousands of verified reviews." },
    { tag: "STARTUP READY",    target: 50,    suffix: "+",      isDecimal: false, title: "Startup partners",           description: "Trusted by growing startups across India for bulk laptop procurement and support." },
    { tag: "LAPTOP MODELS",    target: 500,   suffix: "+",      isDecimal: false, title: "Laptop models in stock",     description: "Dell, HP, Lenovo, Apple, Asus, Acer, MSI and more — all certified, tested, and ready to ship across India." },
  ],
};

/* ─────────────────────────────────────────────────────────────
   AnimatedCounter — isolated, self-managing number animation
   Each instance owns its own rAF loop + IntersectionObserver
───────────────────────────────────────────────────────────── */
const AnimatedCounter = React.memo(({ target, suffix = "", isDecimal = false }) => {
  const counterRef = useAnimatedCounter(target, isDecimal);
  return (
    <>
      <span ref={counterRef} />
      {suffix}
    </>
  );
});
AnimatedCounter.displayName = "AnimatedCounter";

const WhyChooseUsSkeleton = ({ isDark }) => (
  <section className={`py-16 overflow-hidden ${isDark ? "bg-gray-950" : "bg-white"}`}>
    <ShimmerStyles />
    <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-8 space-y-12">
      <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
        <div className="max-w-2xl space-y-4 w-full">
          <ShimmerBox w={96} h={24} rounded={99} dark={isDark} />
          <ShimmerBox w="75%" h={48} rounded={12} dark={isDark} />
          <ShimmerBox w="100%" h={64} rounded={12} dark={isDark} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 min-h-[500px]">
        <ShimmerBox w="100%" h={500} rounded={32} dark={isDark} className="col-span-1 md:col-span-4" />
        <ShimmerBox w="100%" h={500} rounded={32} dark={isDark} className="col-span-1 md:col-span-8" />
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════
   WhyChooseUs
═══════════════════════════════════════════════════════════ */
const WhyChooseUs = () => {
  const containerRef = useRef(null);
  const [data, setData] = useState({ ...DEFAULT_DATA, isVisible: true });
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      const result = await getAboutUsData();
      if (result.success && result.data) {
        setData({
          isVisible:  result.data.isVisible !== undefined ? result.data.isVisible : true,
          header:     result.data.header     || DEFAULT_DATA.header,
          card1:      result.data.card1      || DEFAULT_DATA.card1,
          statsCards: result.data.statsCards?.length >= 8
            ? result.data.statsCards
            : DEFAULT_DATA.statsCards,
        });
      }
      setIsDataLoaded(true);
    };
    fetchData();
  }, []);

  /* ── GSAP: header + card entrance animations only ── */
  useGSAP(
    () => {
      if (!isDataLoaded || !data.isVisible) return;

      gsap.fromTo(
        ".wcu-hdr",
        { y: 36, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.9, stagger: 0.14,
          ease: "power3.out",
          scrollTrigger: { trigger: containerRef.current, start: "top 80%" },
        }
      );

      gsap.fromTo(
        ".bc",
        { y: 28, opacity: 0, scale: 0.97 },
        {
          y: 0, opacity: 1, scale: 1, duration: 0.65, stagger: 0.08,
          ease: "back.out(1.15)",
          scrollTrigger: { trigger: ".bento-root", start: "top 72%" },
        }
      );
    },
    { scope: containerRef, dependencies: [isDataLoaded, data.isVisible] }
  );

  if (!isDataLoaded) {
    return <WhyChooseUsSkeleton isDark={isDark} />;
  }

  if (isDataLoaded && !data.isVisible) {
    return <WhyChooseUsSimple />;
  }

  /* ─── Reusable label ─── */
  const Lbl = ({ text, color }) => (
    <p className="wcu-lbl" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color, marginBottom: 10, minHeight: 28 }}>
      {text}
    </p>
  );

  /* ─── Card color themes ─── */
  const cream  = { background: isDark ? "#2a2016" : "#fbf7f0", border: isDark ? "1px solid #3d3020" : "none" };
  const dark   = { background: isDark ? "#0f0f0f" : "#0a0a0a", border: isDark ? "1px solid #1f1f1f" : "none" };
  const mint   = { background: isDark ? "#0d1f1e" : "#f0faf9", border: isDark ? "1px solid #163d38" : "none" };
  const silver = { background: isDark ? "#141a24" : "#f2f4f7", border: isDark ? "1px solid #1e2b3a" : "none" };
  const bone   = { background: isDark ? "#221c14" : "#fbf7f0", border: isDark ? "1px solid #3a3020" : "1px solid #e5e0d5" };

  const cb = {
    borderRadius: 32, padding: "24px 22px",
    display: "flex", flexDirection: "column",
    justifyContent: "flex-start", height: "100%",
    boxSizing: "border-box", position: "relative",
    overflow: "hidden", gap: 0,
  };

  /* ─── Radial glow helper ─── */
  const Glow = ({ top, left, bottom, right, color = "rgba(255,255,255,0.06)", size = 200 }) => (
    <div style={{
      position: "absolute", pointerEvents: "none",
      top, left, bottom, right,
      width: size, height: size,
      borderRadius: "50%",
      background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
    }} />
  );

  /* ─── Wide-card row layout ─── */
  const WideRow = ({ children }) => (
    <div className="wcu-wide-row" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
      {children}
    </div>
  );

  /* ─── Stat number wrapper ─── */
  const Stat = ({ children, dark: isCardDark }) => (
    <p className="wcu-stat" style={{ fontWeight: 900, color: (isCardDark && !isDark) ? "#fff" : isDark ? "#fff" : "#111", lineHeight: 1, marginBottom: 6 }}>
      {children}
    </p>
  );

  const SubTitle = ({ children, dark: isCardDark }) => (
    <p style={{ fontSize: 14, fontWeight: 600, color: (isCardDark && !isDark) ? "#aaa" : isDark ? "#e5e7eb" : "#374151", marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{children}</p>
  );

  const Desc = ({ children, dark: isCardDark, className }) => (
    <p className={className} style={{ fontSize: 13, color: (isCardDark && !isDark) ? "#666" : isDark ? "#9ca3af" : "#6b7280", lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{children}</p>
  );

  const CircleIcon = ({ color, fill, size = 54 }) => (
    <div className="wcu-icon" style={{ flexShrink: 0, width: size, height: size, borderRadius: "50%", background: fill || "transparent", border: fill ? "none" : `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <CheckCircle size={size * 0.5} color={fill ? "#fff" : color} />
    </div>
  );

  /* Shorthand to make each AnimatedCounter keyed to its target value
     so React remounts (and re-observes) if the API changes the number */
  const SC = (i) => data.statsCards[i];

  return (
    <section ref={containerRef} className="py-16 overflow-hidden bg-white dark:bg-gray-950">
      <style>{`
        /* ── Responsive bento grid ── */
        .bento-root {
          display: grid;
          gap: 12px;
          grid-template-columns: 1fr;
          grid-template-rows: auto;
        }
        .bento-root .bc { min-height: 180px; }

        @media (min-width: 640px) {
          .bento-root {
            grid-template-columns: 1fr 1fr;
            grid-template-rows: auto;
          }
          .bc-wide { grid-column: span 2; }
        }

        @media (min-width: 1024px) {
          .bento-root {
            grid-template-columns: 1fr 1fr 2fr;
            grid-template-rows: 230px 230px 220px;
          }
          .bc-wide { grid-column: auto; }
          .bento-root .bc { min-height: auto; }
        }

        .wcu-stat { font-size: clamp(1.6rem, 5vw, 2.8rem); }

        @media (max-width: 639px) {
          .wcu-icon { display: none !important; }
          .wcu-wide-row { flex-direction: column; }
        }

        @media (min-width: 1024px) {
          .wcu-desc-mh { min-height: 56px; }
        }
      `}</style>

      <div className="container mx-auto px-4">

        {/* ── Header ── */}
        <div className="text-center mb-10">
          <p className="wcu-hdr" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: isDark ? "#d4af37" : "#a87d3e", marginBottom: 10 }}>
            {data.header.tag}
          </p>
          <h2 className="wcu-hdr" style={{ fontSize: "clamp(1.7rem,4.5vw,3rem)", fontWeight: 900, color: isDark ? "#fff" : "#111", lineHeight: 1.2, marginBottom: 12 }}>
            {data.header.title}
          </h2>
          <p className="wcu-hdr" style={{ fontSize: "clamp(13px,2vw,15px)", color: isDark ? "#aaa" : "#6b7280", maxWidth: 500, margin: "0 auto", lineHeight: 1.7 }}>
            {data.header.description}
          </p>
        </div>

        {/* ══════════════════════════════════════════
            BENTO GRID
        ══════════════════════════════════════════ */}
        <div className="bento-root">

          {/* ── ROW 1 ── */}

          {/* R1-C1 | cream — Why Bright Laptop? */}
          <div className="bc" style={{ ...cb, ...cream }}>
            <div>
              <Lbl text={data.card1.tag} color={isDark ? "#d4af37" : "#a87d3e"} />
              <h3 style={{ fontSize: "clamp(1rem,1.8vw,1.3rem)", fontWeight: 900, color: isDark ? "#fff" : "#111", lineHeight: 1.25, marginBottom: 10 }}>
                {data.card1.title}
              </h3>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 7 }}>
                {data.card1.points.map((t, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12, color: isDark ? "#ccc" : "#5a4a30", lineHeight: 1.5 }}>
                    <CheckCircle size={12} style={{ color: isDark ? "#d4af37" : "#a87d3e", flexShrink: 0, marginTop: 2 }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* R1-C2 | dark — 20K+ Customers */}
          <div className="bc" style={{ ...cb, ...dark }}>
            <Glow top="50%" left="50%" color="rgba(255,255,255,0.06)" size={200} />
            <Lbl text={SC(0).tag} color="#555" />
            <div style={{ position: "relative", zIndex: 1 }}>
              <Stat dark>
                <AnimatedCounter key={SC(0).target} target={SC(0).target} suffix={SC(0).suffix} isDecimal={SC(0).isDecimal} />
              </Stat>
              <SubTitle dark>{SC(0).title}</SubTitle>
              <Desc dark>{SC(0).description}</Desc>
            </div>
          </div>

          {/* R1-C3 | wide mint — 49-Point */}
          <div className="bc bc-wide" style={{ ...cb, ...mint }}>
            <Lbl text={SC(1).tag} color="#15803d" />
            <WideRow>
              <div style={{ flex: 1 }}>
                <Stat>
                  <AnimatedCounter key={SC(1).target} target={SC(1).target} suffix={SC(1).suffix} isDecimal={SC(1).isDecimal} />
                </Stat>
                <SubTitle>{SC(1).title}</SubTitle>
                <Desc className="wcu-desc-mh">{SC(1).description}</Desc>
              </div>
              <CircleIcon color="#15803d" fill="#15803d" />
            </WideRow>
          </div>

          {/* ── ROW 2 ── */}

          {/* R2-C1 | dark — 60% Savings */}
          <div className="bc" style={{ ...cb, ...dark }}>
            <Glow top={-40} left={-40} color="rgba(168,125,62,0.3)" size={180} />
            <Lbl text={SC(2).tag} color="#a87d3e" />
            <div style={{ position: "relative", zIndex: 1 }}>
              <Stat dark>
                <AnimatedCounter key={SC(2).target} target={SC(2).target} suffix={SC(2).suffix} isDecimal={SC(2).isDecimal} />
              </Stat>
              <SubTitle dark>{SC(2).title}</SubTitle>
              <Desc dark className="wcu-desc-mh">{SC(2).description}</Desc>
            </div>
          </div>

          {/* R2-C2 | dark — 14-Day */}
          <div className="bc" style={{ ...cb, ...dark }}>
            <Glow bottom={-40} right={-40} color="rgba(168,125,62,0.25)" size={180} />
            <Lbl text={SC(3).tag} color="#a87d3e" />
            <div style={{ position: "relative", zIndex: 1 }}>
              <Stat dark>
                <AnimatedCounter key={SC(3).target} target={SC(3).target} suffix={SC(3).suffix} isDecimal={SC(3).isDecimal} />
              </Stat>
              <SubTitle dark>{SC(3).title}</SubTitle>
              <Desc dark className="wcu-desc-mh">{SC(3).description}</Desc>
            </div>
          </div>

          {/* R2-C3 | wide silver — 12-Month */}
          <div className="bc bc-wide" style={{ ...cb, ...silver }}>
            <Lbl text={SC(4).tag} color="#1d4ed8" />
            <WideRow>
              <div style={{ flex: 1 }}>
                <Stat>
                  <AnimatedCounter key={SC(4).target} target={SC(4).target} suffix={SC(4).suffix} isDecimal={SC(4).isDecimal} />
                </Stat>
                <SubTitle>{SC(4).title}</SubTitle>
                <Desc className="wcu-desc-mh">{SC(4).description}</Desc>
              </div>
              <CircleIcon color="#1d4ed8" />
            </WideRow>
          </div>

          {/* ── ROW 3 ── */}

          {/* R3-C1 | cream — 4.8 Rating */}
          <div className="bc" style={{ ...cb, ...cream }}>
            <Lbl text={SC(5).tag} color={isDark ? "#d4af37" : "#a87d3e"} />
            <div>
              <Stat>
                <AnimatedCounter key={SC(5).target} target={SC(5).target} suffix={SC(5).suffix} isDecimal={SC(5).isDecimal} />
              </Stat>
              <div>
                <p style={{ fontSize: 11, color: isDark ? "#9ca3af" : "#6b7280", marginBottom: 8 }}>{SC(5).description}</p>
                <div style={{ display: "flex", gap: 3 }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} style={{ color: "#f59e0b", fill: i < Math.round(SC(5).target || 0) ? "#f59e0b" : "transparent" }} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* R3-C2 | dark — 50+ Partners */}
          <div className="bc" style={{ ...cb, ...dark }}>
            <Glow top={-30} right={-30} color="rgba(99,102,241,0.25)" size={160} />
            <Lbl text={SC(6).tag} color="#818cf8" />
            <div style={{ position: "relative", zIndex: 1 }}>
              <Stat dark>
                <AnimatedCounter key={SC(6).target} target={SC(6).target} suffix={SC(6).suffix} isDecimal={SC(6).isDecimal} />
              </Stat>
              <SubTitle dark>{SC(6).title}</SubTitle>
              <Desc dark className="wcu-desc-mh">{SC(6).description}</Desc>
            </div>
          </div>

          {/* R3-C3 | wide bone — 500+ Models */}
          <div className="bc bc-wide" style={{ ...cb, ...bone }}>
            <Lbl text={SC(7).tag} color={isDark ? "#d4af37" : "#a87d3e"} />
            <WideRow>
              <div style={{ flex: 1 }}>
                <Stat>
                  <AnimatedCounter key={SC(7).target} target={SC(7).target} suffix={SC(7).suffix} isDecimal={SC(7).isDecimal} />
                </Stat>
                <SubTitle>{SC(7).title}</SubTitle>
                <Desc className="wcu-desc-mh">{SC(7).description}</Desc>
              </div>
              {/* SVG laptop icon */}
              <svg className="wcu-icon" viewBox="0 0 120 80" style={{ flexShrink: 0, width: 90, height: 62, opacity: isDark ? 0.4 : 0.18 }} fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="10" y="5" width="100" height="60" rx="6" stroke={isDark ? "#888" : "#333"} strokeWidth="4" />
                <rect x="20" y="14" width="80" height="44" rx="2" fill={isDark ? "#888" : "#333"} />
                <rect x="0" y="66" width="120" height="8" rx="4" fill={isDark ? "#888" : "#333"} />
                <rect x="44" y="65" width="32" height="4" rx="2" fill={isDark ? "#aaa" : "#555"} />
              </svg>
            </WideRow>
          </div>

        </div>
        {/* end bento-root */}

      </div>
    </section>
  );
};

export default WhyChooseUs;
